import { type FocusEvent, type ReactElement, useMemo } from 'react';
import { Breakpoints } from '@pkgs/components/providers/BreakpointProvider';
import { useBreakpoint } from '@pkgs/components/hooks/useBreakpoint';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { useAutoCompleteStore } from '@pkgs/components/hooks/stores/useAutoCompleteStore';
import { useSearchStore } from '@pkgs/components/hooks/stores/search/useSearchStore';
import { SearchMode } from '@pkgs/components/hooks/stores/search/slices/searchModeSlice';
import { useAiSearch } from '@pkgs/components/hooks/queries/useAiSearch';
import { InputText, type InputTextProps } from '@ev-tech/ui-core-components/InputText';

import { ClearInputButton } from './ClearInputButton';
import { AutoCompleteMobileTrigger } from './AutoCompleteMobileTrigger';
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner';
import { CyclingLoadingText } from '../CyclingLoadingText/CyclingLoadingText';

import styles from './AutoCompleteInput.module.css';

type AutoCompleteInputContentProps = {
  inputProps: InputTextProps;
  isFetching?: boolean;
  onClearInput?: () => void;
  setInputRefs: (element: HTMLInputElement | null) => void;
  handleFocus: (e: FocusEvent<HTMLInputElement>) => void;
  handleBlur: (e: FocusEvent<HTMLInputElement>) => void;
};

export const AutoCompleteInputContent = ({
  inputProps,
  isFetching,
  onClearInput,
  setInputRefs,
  handleFocus,
  handleBlur,
}: AutoCompleteInputContentProps): ReactElement => {
  const { t } = useAppContext();
  const { breakpoint = 0, isMobile } = useBreakpoint();
  const isMobileModalOpen = useAutoCompleteStore((state) => state.isMobileModalOpen);
  const setIsMobileModalOpen = useAutoCompleteStore((state) => state.setIsMobileModalOpen);
  const searchMode = useSearchStore((state) => state.searchMode);
  const { isPending: isAiSearchFetching } = useAiSearch();

  const shouldShowMobileTriggerButton = breakpoint < Breakpoints.TABLET_LANDSCAPE && !isMobileModalOpen;

  const getPlaceholderText = useMemo(() => {
    if (isMobileModalOpen) {
      return isMobile ? t('search.searchbar.mobile.label') : t('search.searchbar.label');
    }
    if (isMobile) {
      return searchMode === SearchMode.CLASSIC ? t('search.searchbar.mobile.label') : t('search.aiSearch.aiMode.promptInput.placeholder');
    }
    return t('search.searchbar.label');
  }, [isMobile, t, searchMode, isMobileModalOpen]);

  const getDisplayEndAdornment = useMemo(() => {
    if (isFetching) {
      return <LoadingSpinner variant="dark" />;
    } else if (isAiSearchFetching) {
      return <LoadingSpinner variant="ai" />;
    }
    return <ClearInputButton onClick={onClearInput} data-testid="search-components_autocomplete_clear-btn" />;
  }, [isFetching, isAiSearchFetching, onClearInput]);

  if (isAiSearchFetching && !isMobileModalOpen) {
    return <CyclingLoadingText className={styles.cyclingLoadingText} data-testid="autocomplete-cycling-loading-text" />;
  } else if (shouldShowMobileTriggerButton) {
    return (
      <AutoCompleteMobileTrigger
        displayText={inputProps.value || getPlaceholderText}
        isEmpty={!inputProps.value}
        isAiSearchFetching={isAiSearchFetching}
        onClick={() => setIsMobileModalOpen(true)}
        ariaLabel={t('search.searchbar.classicsearch.input.label')}
        endAdornment={getDisplayEndAdornment}
      />
    );
  }

  return (
    <InputText
      {...inputProps}
      className={styles.inputText}
      enterKeyHint="search"
      disabled={isAiSearchFetching}
      aria-label={t('search.searchbar.classicsearch.input.label')}
      placeholder={getPlaceholderText}
      ref={setInputRefs}
      endAdornment={getDisplayEndAdornment}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );
};
